"use client";

import { Check, ChevronsUpDown, Loader2, Plus, Search } from "lucide-react";
import * as React from "react";

import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Right-aligned muted detail — "7 people", "10 KRAs · 100%", a designation. */
  hint?: string;
  /** Second line under the label, for an email or a department path. */
  description?: string;
  /** Section heading. Options sharing one render under it, in the order given. */
  group?: string;
  /** Avatar or icon shown before the label. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface ComboboxProps {
  value: string | null;
  /**
   * May return a promise when picking does real work — assigning, creating from a
   * suggestion. The popup then holds itself open until it settles, so it never closes
   * on a request that has not landed.
   */
  onChange: (value: string) => void | Promise<unknown>;
  options: ComboboxOption[];
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;

  /**
   * Create the typed name and select it. May return a promise — the popup stays open
   * with a working state until it resolves, so the user sees the record land rather
   * than a dropdown that closed on a request that had not finished.
   *
   * Omit and the field is select-only: the search still works, there is just nothing
   * new to make from it.
   */
  onCreate?: (name: string) => void | Promise<unknown>;
  /** Verb on the create row: `Create KRA` renders `Create KRA "Sr. Analyst"`. */
  createLabel?: string;
  /**
   * Whether this user may create. The courtesy copy of the API's `@Roles` guard —
   * false hides the row rather than offering a click that 403s. Never the control.
   */
  canCreate?: boolean;
  /** Shortest name worth offering to create. Stops `+ Create "En"` mid-word. */
  minCreateLength?: number;

  /** Server-side search. Called debounced, so a burst of keystrokes is one request. */
  onSearchChange?: (query: string) => void;
  searchDebounceMs?: number;
  loading?: boolean;
  /** Search failed. Shown in place of the list, so a blank popup is never a lie. */
  error?: string | null;

  /**
   * Clearing is not a prop.
   *
   * Every picker here already has an explicit "Not set" / "All departments" option,
   * which is the same affordance as an X, reachable by keyboard, and one the value can
   * actually round-trip through. An X inside the trigger would be a button nested in a
   * button — invalid, and unreachable by Tab.
   */

  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  className?: string;
  contentClassName?: string;
  align?: "start" | "center" | "end";
  /** Replaces the default Select-shaped button. Must accept a ref (`asChild`). */
  trigger?: React.ReactNode;
}

/**
 * Search-or-create picker, the shape GitHub's branch switcher uses.
 *
 * One field does three jobs a `Select` cannot: it types-to-filter, so 71 people or 44
 * designations are one keystroke away instead of a scroll; it never renders the whole
 * list into the page, so a long list costs no page height; and when nothing matches,
 * the text already typed becomes the new record rather than sending the user off to
 * another screen to create it and come back.
 *
 * Use it wherever the options come from the database and can grow past a handful. A
 * short fixed enum — Role, quarter, a status filter — stays a `Select`: a search box
 * over five permanent choices is friction, not help.
 *
 * `onCreate` is deliberately opt-in per call site. Creating a department from a
 * dropdown is fine; creating an *employee* from one is not, and the difference is a
 * judgement about the record, not about the widget.
 *
 * Accessibility follows the APG combobox-with-listbox pattern: DOM focus stays on the
 * search input, the active row is tracked with `aria-activedescendant` rather than by
 * moving focus, and the list is a real `listbox`/`option` tree. Moving focus into the
 * list instead is the usual mistake — it strands screen readers away from the field
 * they are typing into.
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder = "Select",
  searchPlaceholder = "Search",
  /** Reads as a sentence with the query appended: "No department matches “Ops”." */
  emptyText = "Nothing matches",
  onCreate,
  createLabel = "Create",
  canCreate = true,
  minCreateLength = 2,
  onSearchChange,
  searchDebounceMs = 250,
  loading = false,
  error = null,
  disabled,
  id,
  "aria-label": ariaLabel,
  className,
  contentClassName,
  align = "start",
  trigger,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(0);
  /** A pick or a create is in flight. Holds the popup open until the server answers. */
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const rowRefs = React.useRef<(HTMLElement | null)[]>([]);
  const listId = React.useId();

  const typed = query.trim();
  const needle = normalize(query);

  // Server-side search: one request after the typing stops, not one per keystroke.
  // The callback is held in a ref so an inline arrow function from the caller cannot
  // restart the timer on every render — which would debounce the request to never.
  const searchHandler = React.useRef(onSearchChange);
  searchHandler.current = onSearchChange;
  React.useEffect(() => {
    if (!searchHandler.current) return;
    const timer = setTimeout(() => searchHandler.current?.(typed), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [typed, searchDebounceMs]);

  // Filtering is local unless the caller is searching server-side, in which case the
  // options handed in are already the answer and filtering them again would hide rows
  // the server matched on a field this component cannot see.
  const matches = React.useMemo(() => {
    if (onSearchChange || needle === "") return options;
    return options.filter(
      (option) =>
        normalize(option.label).includes(needle) ||
        normalize(option.description ?? "").includes(needle) ||
        normalize(option.hint ?? "").includes(needle),
    );
  }, [options, needle, onSearchChange]);

  /**
   * Duplicate prevention, before the request rather than after it.
   *
   * Compared on a normalized name — case-folded, whitespace collapsed — so typing
   * "engineering" against an existing "Engineering" offers the existing row instead of
   * a second department nobody can tell apart from the first. Deliberately stricter
   * than the database's exact-match unique index: suppressing a create the API would
   * have allowed is recoverable, creating a near-duplicate is not.
   *
   * It is checked against `options`, not `matches`, so a name filtered out of view
   * still blocks the create row.
   */
  const duplicate = options.some((option) => normalize(option.label) === needle);
  const creatable =
    onCreate !== undefined &&
    canCreate &&
    !duplicate &&
    !loading &&
    typed.length >= minCreateLength;

  // Indices here are the same indices the rendered rows carry, so `aria-activedescendant`
  // and the arrow keys can never point at different things.
  const rows: Array<ComboboxOption | "create"> = React.useMemo(
    () => [...matches, ...(creatable ? (["create"] as const) : [])],
    [matches, creatable],
  );

  const selected = options.find((option) => option.value === value);

  // Options arriving late from the server must not leave the highlight past the end.
  React.useEffect(() => {
    setActive((i) => (i >= rows.length ? 0 : i));
  }, [rows.length]);
  React.useEffect(() => setActive(0), [needle]);
  React.useEffect(() => {
    if (open) rowRefs.current[active]?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function close() {
    setOpen(false);
    setQuery("");
    setActionError(null);
  }

  /**
   * Run a pick or a create, then close — but only once it has actually finished.
   *
   * Awaited, so the dropdown closes on a record that exists rather than on one the
   * server may still refuse. A failure keeps the popup open with the reason in it,
   * because the alternative is a closed dropdown and a value that never changed.
   */
  async function run(action: () => void | Promise<unknown>, fallback: string) {
    if (busy) return;
    setActionError(null);
    try {
      setBusy(true);
      await action();
      close();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  function pick(option: ComboboxOption) {
    if (option.disabled) return;
    void run(() => onChange(option.value), "That did not go through. Try again.");
  }

  function create() {
    if (!onCreate) return;
    void run(() => onCreate(typed), "Could not create that. Try again.");
  }

  function activate(index: number) {
    const row = rows[index];
    if (row === "create") create();
    else if (row) pick(row);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => (rows.length === 0 ? 0 : Math.min(i + 1, rows.length - 1)));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(Math.max(rows.length - 1, 0));
        break;
      case "Enter":
        event.preventDefault();
        activate(active);
        break;
      case "Tab":
        // Tab commits nothing: leaving a field is not choosing a value, and a stray
        // Tab that silently picked the top row would be very hard to notice.
        close();
        break;
      default:
        break;
    }
  }

  let heading: string | undefined;
  const activeId = rows.length > 0 ? `${listId}-row-${active}` : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else close();
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        {trigger ?? (
          <button
            id={id}
            type="button"
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              className,
            )}
          >
            <span className={cn("line-clamp-1 text-left", !selected && "text-muted-foreground")}>
              {selected?.label ?? placeholder}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent
        align={align}
        // Never wider than the viewport, and long names truncate rather than push the
        // panel off a phone screen.
        className={cn(
          "w-[var(--radix-popover-trigger-width)] min-w-56 max-w-[calc(100vw-2rem)] p-0",
          contentClassName,
        )}
        // Radix focuses the panel itself by default, which would leave the user one Tab
        // away from the field this whole component exists to put them in.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded
            aria-autocomplete="list"
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-label={ariaLabel ?? searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {loading && <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />}
        </div>

        <div role="listbox" id={listId} className="max-h-64 overflow-y-auto p-1">
          {error && <p className="px-2 py-6 text-center text-xs text-destructive">{error}</p>}

          {!error &&
            matches.map((option, index) => {
              const newGroup = option.group !== undefined && option.group !== heading;
              heading = option.group;
              return (
                <React.Fragment key={option.value}>
                  {newGroup && (
                    <p
                      role="presentation"
                      className="px-2 pb-1 pt-2 text-xs text-muted-foreground"
                    >
                      {option.group}
                    </p>
                  )}
                  <div
                    ref={(node) => {
                      rowRefs.current[index] = node;
                    }}
                    id={`${listId}-row-${index}`}
                    role="option"
                    aria-selected={option.value === value}
                    aria-disabled={option.disabled}
                    onMouseEnter={() => !option.disabled && setActive(index)}
                    onClick={() => pick(option)}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
                      option.disabled && "pointer-events-none opacity-50",
                      index === active && "bg-accent text-accent-foreground",
                    )}
                  >
                    <Check
                      className={cn(
                        "size-4 shrink-0",
                        option.value === value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.icon}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.description && (
                        <span className="block truncate text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      )}
                    </span>
                    {option.hint && (
                      <span className="shrink-0 text-xs text-muted-foreground">{option.hint}</span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

          {!error && creatable && (
            <div
              ref={(node) => {
                rowRefs.current[matches.length] = node;
              }}
              id={`${listId}-row-${matches.length}`}
              role="option"
              aria-selected={false}
              onMouseEnter={() => setActive(matches.length)}
              onClick={create}
              className={cn(
                "mt-1 flex w-full cursor-pointer items-center gap-2 rounded-sm border-t px-2 py-2 text-left text-sm",
                active === matches.length && "bg-accent text-accent-foreground",
              )}
            >
              {busy ? (
                <Loader2 className="size-4 shrink-0 animate-spin" />
              ) : (
                <Plus className="size-4 shrink-0" />
              )}
              <span className="truncate">
                {busy ? "Creating" : createLabel}{" "}
                <span className="font-medium">&ldquo;{typed}&rdquo;</span>
                {busy && "…"}
              </span>
            </div>
          )}

          {!error && !loading && matches.length === 0 && !creatable && (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              {typed === "" ? emptyText : `${emptyText} “${typed}”.`}
            </p>
          )}

          {!error && loading && matches.length === 0 && (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">Searching…</p>
          )}
        </div>

        {actionError && (
          <p className="border-t px-3 py-2 text-xs text-destructive" role="alert">
            {actionError}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The comparison key for "is this the same name".
 *
 * Case-folded and whitespace-collapsed, because " Sales  Ops " and "sales ops" are one
 * department to a human and two rows to a unique index.
 */
function normalize(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, " ");
}
